import Card from '@/components/Card/Card';
import { InlineAlert } from '@/components/alert';
import B2BProductCard from '@/components/b2BproductCard/B2BProductCard';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import { RichText } from '@/components/richText/RichText';
import { ProductCategory } from '@/helper/enums';
import tracking, { Currency, EventCategory, EventSource, type TrackingInformation } from '@/helper/tracking';
import { isEqual } from '@/helper/utils';
import { useB2BStore } from '@/providers/B2BStoreProvider';
import type { PageB2BStoryblok } from '@/types/component-types-sb';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';

import { getFalsifiedPrices } from './B2BFormHandler';
import classes from './B2BTariffOverview.module.scss';
import type { B2BFormData } from './PageB2B';

export interface ConsumptionFormProps {
  pageB2BData: PageB2BStoryblok;
}

export interface B2BTariff {
  tarifname: string;
  price: string;
  duration: string;
  showPrice: boolean;
  greenEnergy: boolean;
}

const B2BTariffOverview: React.FC<ConsumptionFormProps> = ({ pageB2BData }) => {
  const [tariffs, setTariffs] = useState<B2BTariff[] | undefined>();
  const [trackingInformation, setTrackingInformation] = useState<TrackingInformation[]>([]);
  const [priceParams, setPriceParams] = useState<{
    annualConsumption: string;
    startDate: string;
    greenEnergy: boolean;
    loadProfile: string;
  }>();

  const updateTrackingInformation = useB2BStore((s) => s.updateTrackingInformation);
  const formik = useFormikContext<B2BFormData>();

  const fetchExternalPriceData = async (values: B2BFormData) => {
    if (values.annualConsumption && values.startDate && values.loadProfile) {
      const formattedDate = new Date(values.startDate).toLocaleDateString('de-DE');

      try {
        const response = await getFalsifiedPrices(
          Number(values.annualConsumption),
          formattedDate,
          values.greenEnergy,
          values.loadProfile
        );

        const tariffs: B2BTariff[] = [];

        const getPrice = (duration: '12' | '24' | '36') => {
          switch (duration) {
            case '12':
              return response.priceFor12Months ?? '';
            case '24':
              return response.priceFor24Months ?? '';
            case '36':
              return response.priceFor36Months ?? '';
          }
        };

        pageB2BData.tariffs?.forEach((tariff) => {
          if (!!tariff.greenEnergy === !!values.greenEnergy) {
            tariffs.push({
              tarifname: tariff.name ?? '',
              price: getPrice(tariff.duration),
              showPrice: !tariff.hide_price,
              duration: tariff.duration,
              greenEnergy: !!tariff.greenEnergy,
            });
          }
        });

        setTariffs([...tariffs.filter((tariff) => !!tariff.price)]);
      } catch (error) {
        console.error('Error fetching external price data:', error);
      }
    }
  };

  useEffect(() => {
    const { annualConsumption, startDate, greenEnergy, loadProfile } = formik.values;
    const newValue = { annualConsumption, startDate, greenEnergy, loadProfile };
    if (!isEqual(priceParams, newValue)) {
      setPriceParams(newValue);
      fetchExternalPriceData(formik.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.annualConsumption, formik.values.startDate, formik.values.greenEnergy, formik.values.loadProfile]);

  useEffect(() => {
    if (tariffs) {
      const trackingItems: TrackingInformation[] = tariffs.map((tariff, index) => ({
        itemId: `months${tariff.duration}`,
        itemName: tariff.tarifname,
        index,
        itemListName: EventSource.B2BOverview,
        itemCategory: EventCategory.B2BCommodity,
        itemCategory2: ProductCategory.Electricity,
        ...(tariff.showPrice && {
          price: parseFloat(tariff.price),
          currency: Currency.EUR,
        }),
        quantity: parseInt(tariff.duration),
      }));

      setTrackingInformation(trackingItems);
      tracking.itemListViewImpression(trackingItems);
    }
  }, [tariffs]);

  useEffect(() => {
    if (formik.values.selectedTariff && trackingInformation) {
      // todo refacor
      const selectedTrackingitem = trackingInformation.filter(
        (trackingInformation) => trackingInformation.itemId === `months${formik.values.selectedTariff.duration}`
      );

      if (selectedTrackingitem && selectedTrackingitem.length > 0) {
        if (selectedTrackingitem[0]) {
          tracking.productClick(selectedTrackingitem[0]);
          tracking.addToCart(selectedTrackingitem);
          tracking.beginCheckout(selectedTrackingitem);

          if (updateTrackingInformation) {
            updateTrackingInformation(selectedTrackingitem);
          }
        }
      }
    }
  }, [formik.values.selectedTariff]);

  return (
    <div className={classes.B2BTariffOverview}>
      <Card className={classes.Card} variant="Box">
        <LoadingBackdrop loading={!tariffs} type="card" />

        {!!tariffs && (
          <>
            {!!pageB2BData.form_tariffs_title && <h2 className={classes.Headline}>{pageB2BData.form_tariffs_title}</h2>}
            <div className={classes.ProductCards}>
              {tariffs?.map((tariff) => (
                <B2BProductCard
                  key={tariff.duration}
                  tariff={tariff}
                  activeTariff={formik.values.selectedTariff.duration === tariff.duration}
                  onSelectTariff={() => formik.setFieldValue('selectedTariff', tariff)}
                  onDeselectTariff={() =>
                    formik.setFieldValue('selectedTariff', {
                      tarifname: '',
                      price: '',
                      duration: '',
                      showPrice: false,
                      greenEnergy: false,
                    })
                  }
                  pricePlaceholder={pageB2BData.price_placeholder}
                />
              ))}
            </div>
            <InlineAlert severity="info" variant="outlined">
              <RichText text={pageB2BData.tariffs_info_alert} />
            </InlineAlert>
          </>
        )}
      </Card>
    </div>
  );
};

export default B2BTariffOverview;
